import { InviteLinkType, OnboardingStep, UserModules } from '../types';

const ONBOARDING_DATA_LS_KEY = 'onboarding';
const ONBOARDING_STEP_LS_KEY = 'onboardingStep';
const PENDING_INVITE_LS_KEY = 'pendingInvite';

export interface PendingInvite {
  inviteToken: string;
  type: InviteLinkType;
  companyId?: string;
  companyName?: string;
  payScheduleId?: string;
  teamId?: string;
  modules?: Partial<UserModules>;
}

// In-memory shadow used ONLY for writes that failed to reach localStorage
// (Edge Tracking Prevention, InPrivate, blocked site data, quota exceeded).
// Never a mirror of working storage — otherwise cleared keys (e.g. auth
// tokens on logout) would be resurrected from memory.
const memoryStore = new Map<string, string | undefined>();
let storageWarned = false;

const warnStorageUnavailable = (e: unknown) => {
  if (storageWarned) return;
  storageWarned = true;
  console.warn('localStorage unavailable — falling back to in-memory storage for this tab', e);
};

export const getFromLS = (key: string) => {
  let fromLS: string | null | undefined;
  try {
    if (global.localStorage) {
      fromLS = global.localStorage.getItem(key);
    }
  } catch (e) {
    warnStorageUnavailable(e);
  }
  if (fromLS === null || fromLS === undefined) {
    fromLS = memoryStore.get(key);
  }
  let data: any;
  try {
    data = JSON.parse(fromLS || '') || undefined;
  } catch (e) {}
  return data;
};

export const saveToLS = (key: string, value: any) => {
  const serialized = JSON.stringify(value);
  try {
    if (global.localStorage) {
      global.localStorage.setItem(key, serialized);
      memoryStore.delete(key);
      return;
    }
  } catch (e) {
    warnStorageUnavailable(e);
  }
  memoryStore.set(key, serialized);
};

export const clearLSExcept = (keysToKeep: string[]) => {
  try {
    if (global.localStorage) {
      const kept: Array<[string, string]> = [];
      keysToKeep.forEach(key => {
        const value = global.localStorage.getItem(key);
        if (value != null) kept.push([key, value]);
      });
      global.localStorage.clear();
      kept.forEach(([key, value]) => global.localStorage.setItem(key, value));
    }
  } catch (e) {
    warnStorageUnavailable(e);
  }
  const keptMemory = keysToKeep
    .map(key => [key, memoryStore.get(key)] as const)
    .filter(([, value]) => value !== undefined);
  memoryStore.clear();
  keptMemory.forEach(([key, value]) => memoryStore.set(key, value));
};

export const saveOnboardingDataToLS = (data: any, stepNumber: number) => {
  saveToLS(ONBOARDING_STEP_LS_KEY, stepNumber);
  saveToLS(ONBOARDING_DATA_LS_KEY, data);
};

export const setOnboardingData = (
  setValue: Function,
  setActiveStep: Function,
  nextStep: Function,
  steps: OnboardingStep[]
) => {
  const onboardingData = getFromLS(ONBOARDING_DATA_LS_KEY);
  const onboardingStepNumber = getFromLS(ONBOARDING_STEP_LS_KEY);
  let status = false;
  const activeStep = steps[onboardingStepNumber];
  if (activeStep) {
    setActiveStep(activeStep);
    nextStep(onboardingStepNumber);
    status = true;
  }
  if (onboardingData) {
    Object.keys(onboardingData).forEach((key: typeof onboardingData) => setValue(key, onboardingData[key]));
  }
  return status;
};

export const clearOnboardingLSData = () => {
  saveToLS(ONBOARDING_DATA_LS_KEY, undefined);
  saveToLS(ONBOARDING_STEP_LS_KEY, undefined);
};

export const savePendingInvite = (invite: PendingInvite) => {
  saveToLS(PENDING_INVITE_LS_KEY, invite);
};

export const readPendingInvite = (): PendingInvite | undefined => {
  const value = getFromLS(PENDING_INVITE_LS_KEY);
  return value && typeof value === 'object' && value.inviteToken ? (value as PendingInvite) : undefined;
};

export const clearPendingInvite = () => {
  saveToLS(PENDING_INVITE_LS_KEY, undefined);
};
